import type { MarketVariants } from '@/lib/i18n'

export type Slide3TechSupportCopy = {
  title: string
  warningHeader: string
  warningBodyBold: string
  warningBody: string
  /** "Call Microsoft Support immediately" is rendered as four spans so the
   *  brand highlights under "gain trust", the verb + "immediately" under
   *  "trigger emotion" and the "Support" fragment under "push action". Each
   *  variant must keep the four-piece split reading naturally when joined. */
  callPrefix: string
  brandName: string
  callMid: string
  callImmediately: string
  /** Fake support number. Format must be plausible toll-free for the market
   *  (US/CA: 1-800, GB: 0800, AU: 1800). Rendered LTR in every language. */
  fakeNumber: string
  doNotClose: string

  trustItem1: string
  trustItem2: string
  pressureFear: string
  pressureUrgency: string
  actionItem: string

  trustLabel: string
  emotionLabel: string
  actionLabel: string
  revealButton: string
}

export const copy: MarketVariants<Slide3TechSupportCopy> = {
  US: {
    title: 'Fake security alerts',
    warningHeader: 'Microsoft Security Warning',
    warningBodyBold: 'Your computer has been compromised.',
    warningBody:
      ' A virus has been detected that is putting your personal files and banking data at risk.',
    callPrefix: 'Call ',
    brandName: 'Microsoft',
    callMid: ' Support ',
    callImmediately: 'immediately',
    fakeNumber: '1-800-389-5712',
    doNotClose: 'Do NOT close this window.',

    trustItem1: 'Claiming to be Microsoft',
    trustItem2: 'Official "Security Warning" wording',
    pressureFear: 'Fear: "compromised", "data at risk"',
    pressureUrgency: 'Urgency: "Call immediately", "Do NOT close"',
    actionItem: 'Call the fake support number',

    trustLabel: 'Gain trust',
    emotionLabel: 'Trigger emotion',
    actionLabel: 'Push action',
    revealButton: 'Reveal the pattern',
  },
  GB: {
    // GB toll-free is 0800. Number kept identical-looking to other markets so
    // the visual doesn't shift, just the prefix.
    fakeNumber: '0800-389-5712',
  },
  // CA inherits US (1-800 covers North American Numbering Plan).
  AU: {
    // AU toll-free is 1800 (no hyphen between 1 and 800; 6-digit body).
    fakeNumber: '1800 389 712',
  },
  // Qatar: 800 toll-free numbers are uncommon there, so the pop-up shows a
  // Doha landline in the +974 4xxx xxxx shape that local support desks use.
  // Microsoft stays Microsoft (the impersonated brand is global).
  QA: {
    fakeNumber: '+974 4438 9571',
  },
  ar: {
    title: 'تنبيهات أمنية مزيفة',
    warningHeader: 'تحذير أمني من مايكروسوفت',
    warningBodyBold: 'تم اختراق جهازك.',
    warningBody:
      ' تم رصد فيروس يعرّض ملفاتك الشخصية وبياناتك المصرفية للخطر.',
    // Joined: "اتصل بدعم مايكروسوفت الفني فوراً"
    callPrefix: 'اتصل بدعم ',
    brandName: 'مايكروسوفت',
    callMid: ' الفني ',
    callImmediately: 'فوراً',
    fakeNumber: '+974 4438 9571',
    doNotClose: 'لا تغلق هذه النافذة أبداً.',

    trustItem1: 'انتحال صفة مايكروسوفت',
    trustItem2: 'صياغة رسمية: "تحذير أمني"',
    pressureFear: 'الخوف: "تم اختراق جهازك"، "بياناتك المصرفية للخطر"',
    pressureUrgency: 'الاستعجال: "اتصل فوراً"، "لا تغلق هذه النافذة"',
    actionItem: 'الاتصال برقم الدعم المزيف',

    trustLabel: 'كسب الثقة',
    emotionLabel: 'إثارة المشاعر',
    actionLabel: 'الدفع إلى التصرف',
    revealButton: 'اكشف النمط',
  },
}
